import React from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  BatteryWarning,
  Clock,
  History,
  RadioTower,
  Trophy,
  Users,
  WifiOff,
  Zap,
  Timer,
} from "lucide-react";
import { formatDuration } from "utils/caseDashboard";
import {
  SEVERITY_COLORS,
  SEVERITY_TEXT,
  severityIndex,
} from "utils/caseLiveStats";
import { fmtDateTime, fmtTime } from "../parts";
import { Sparkline, gText, useAgo } from "./shared";

/** Title row: what you're looking at, for how long, and how fresh it is. */
export const LiveHeader = ({ stats, sessionId, replay, now, updatedAt }) => {
  const { t } = useTranslation();
  const fresh = updatedAt != null && now - updatedAt < 8000;
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2.5">
          {replay ? (
            <History size={18} className="text-[#6B7280]" />
          ) : (
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#16A34A] opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#16A34A]" />
            </span>
          )}
          <h1 className="text-[22px] font-bold text-[#111827]">
            {replay
              ? t("caseDashboard.live.replay")
              : t("caseDashboard.tabs.live")}
          </h1>
          {stats.durationMs != null && (
            <span className="flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[13px] font-semibold tabular-nums text-[#374151] shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
              <Timer size={13} className="text-[#9CA3AF]" />
              {clock(stats.durationMs)}
            </span>
          )}
        </div>
        <p className="mt-1 truncate text-[13px] text-[#6B7280]">
          {replay && stats.ended
            ? t("caseDashboard.live.ranFromTo", {
                from: fmtDateTime(new Date(stats.started)),
                to: fmtTime(new Date(stats.ended)),
              })
            : t("caseDashboard.live.started", {
                time: fmtDateTime(new Date(stats.started)),
              })}
          <span className="text-[#D1D5DB]"> · </span>
          <span className="font-mono text-[12px] text-[#9CA3AF]">
            {sessionId}
          </span>
        </p>
      </div>
      {!replay && (
        <span
          className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
            fresh
              ? "bg-[#DCFCE7] text-[#15803D]"
              : "bg-[#FEF3E7] text-[#B4531A]"
          }`}
          title={updatedAt ? new Date(updatedAt).toLocaleTimeString() : ""}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              fresh ? "bg-[#16A34A]" : "bg-[#E07A3A]"
            }`}
          />
          {updatedAt == null
            ? t("caseDashboard.common.loading")
            : fresh
            ? t("caseDashboard.live.updatedLive")
            : t("caseDashboard.live.updatedAgo", {
                time: formatDuration((now - updatedAt) / 1000),
              })}
        </span>
      )}
    </div>
  );
};

const clock = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
};

const TONE = {
  bad: {
    icon: "text-[#DC2626]",
    title: "text-[#991B1B]",
    rail: "bg-[#DC2626]",
  },
  warn: {
    icon: "text-[#E07A3A]",
    title: "text-[#9A4A17]",
    rail: "bg-[#E07A3A]",
  },
  info: {
    icon: "text-[#6B7280]",
    title: "text-[#374151]",
    rail: "bg-[#D1D5DB]",
  },
};
const ALERT_ICON = {
  noneHeard: WifiOff,
  silent: WifiOff,
  quiet: RadioTower,
  battery: BatteryWarning,
  longSession: Clock,
};

/** What needs attention: one compact row per problem, players named. */
export const AlertList = ({ alerts, now, onSelect }) => {
  const { t } = useTranslation();
  const ago = useAgo();
  if (!alerts.length) return null;
  const worst = alerts[0].tone;
  return (
    <div
      className={`overflow-hidden rounded-xl border bg-white ${
        worst === "bad"
          ? "border-[#FECACA]"
          : worst === "warn"
          ? "border-[#FDE3C8]"
          : "border-[#F3F4F6]"
      }`}
    >
      <div
        className={`flex items-center gap-2 px-4 py-2 text-[12px] font-semibold ${
          worst === "bad"
            ? "bg-[#FEF2F2] text-[#991B1B]"
            : worst === "warn"
            ? "bg-[#FFF8F1] text-[#9A4A17]"
            : "bg-[#F9FAFB] text-[#374151]"
        }`}
      >
        <AlertTriangle size={14} />
        {t("caseDashboard.live.alerts.heading", { count: alerts.length })}
      </div>
      <AnimatePresence initial={false}>
        {alerts.map((a) => {
          const s = TONE[a.tone];
          const Icon = ALERT_ICON[a.kind] || AlertTriangle;
          const n = (a.players || []).length;
          const vars = {
            count: n,
            time: a.ms != null ? formatDuration(a.ms / 1000) : "",
          };
          return (
            <motion.div
              key={a.id}
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, height: 0 }}
              className="relative flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[#F3F4F6] py-2.5 pl-5 pr-4"
            >
              <span
                className={`absolute inset-y-2 left-0 w-1 rounded-r ${s.rail}`}
              />
              <div className="flex min-w-[260px] flex-1 items-start gap-2.5">
                <Icon size={16} className={`mt-0.5 shrink-0 ${s.icon}`} />
                <div className="min-w-0">
                  <p className={`text-[13px] font-semibold ${s.title}`}>
                    {t(`caseDashboard.live.alerts.${a.kind}.title`, vars)}
                  </p>
                  <p className="text-[12px] text-[#6B7280]">
                    {t(`caseDashboard.live.alerts.${a.kind}.body`, vars)}
                  </p>
                </div>
              </div>
              {n > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {a.players.map((p) => (
                    <button
                      type="button"
                      key={p.key}
                      onClick={() => onSelect(p.key)}
                      className="flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-2 py-0.5 text-[12px] font-medium text-[#374151] transition duration-150 hover:border-[#D1D5DB] hover:bg-[#F9FAFB]"
                    >
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: p.color }}
                      />
                      {p.name || p.wearable}
                      <span className="text-[#9CA3AF]">
                        {a.kind === "battery"
                          ? `${p.battery}%`
                          : ago(p.lastContact, now)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

const Tile = ({ icon: Icon, label, children, accent }) => (
  <div className="relative overflow-hidden rounded-xl border border-[#F3F4F6] bg-white p-4">
    {accent && (
      <span
        className="absolute inset-y-0 left-0 w-1"
        style={{ backgroundColor: accent }}
      />
    )}
    <div className="mb-2 flex items-center gap-1.5">
      <Icon size={14} className="text-[#6B7280]" />
      <span className="text-xs text-[#6B7280]">{label}</span>
    </div>
    {children}
  </div>
);

/** Number that pops briefly when it changes. */
const Big = ({ value, className = "" }) => (
  <motion.span
    key={value}
    initial={{ scale: 1.12, color: "#E07A3A" }}
    animate={{ scale: 1, color: "#111827" }}
    transition={{ duration: 0.5 }}
    className={`inline-block origin-left text-[28px] font-bold tabular-nums leading-none ${className}`}
  >
    {value}
  </motion.span>
);

/** The four headline numbers. */
export const KpiRow = ({ stats, replay, now }) => {
  const { t } = useTranslation();
  const ago = useAgo();
  const { statusCounts: sc, active } = stats;
  const segments = [
    ["live", "#16A34A"],
    ["quiet", "#E07A3A"],
    ["silent", "#DC2626"],
    ["never", "#D1D5DB"],
  ];
  const big = stats.biggest;
  const bigColor = big ? SEVERITY_COLORS[severityIndex(big.g)] : undefined;
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Tile
        icon={Users}
        label={
          replay
            ? t("caseDashboard.live.kpi.playersAll")
            : t("caseDashboard.live.kpi.players")
        }
      >
        <div className="flex items-baseline gap-2">
          <Big value={replay ? active.length : sc.live} />
          {!replay && (
            <span className="text-sm text-[#6B7280]">
              {t("caseDashboard.live.kpi.ofLive", { count: active.length })}
            </span>
          )}
        </div>
        {!replay && active.length > 0 && (
          <>
            <div className="mt-3 flex h-1.5 overflow-hidden rounded-full bg-[#F3F4F6]">
              {segments.map(([k, c]) =>
                sc[k] ? (
                  <span
                    key={k}
                    style={{
                      width: `${(sc[k] / active.length) * 100}%`,
                      backgroundColor: c,
                    }}
                  />
                ) : null
              )}
            </div>
            <p className="mt-1.5 text-[11px] text-[#6B7280]">
              {segments
                .filter(([k]) => sc[k])
                .map(([k]) =>
                  t(`caseDashboard.live.kpi.count_${k}`, { count: sc[k] })
                )
                .join(" · ")}
            </p>
          </>
        )}
      </Tile>

      <Tile icon={Zap} label={t("caseDashboard.live.kpi.impacts")}>
        <div className="flex items-baseline gap-2">
          <Big value={stats.total} />
          {!replay && stats.recent > 0 && (
            <span className="text-[12px] font-medium text-[#16A34A]">
              {t("caseDashboard.live.kpi.lastFive", { count: stats.recent })}
            </span>
          )}
        </div>
        <Sparkline values={stats.sparkline} className="mt-2" />
      </Tile>

      <Tile
        icon={Trophy}
        label={t("caseDashboard.live.kpi.biggest")}
        accent={bigColor}
      >
        {big ? (
          <>
            <Big value={gText(t, big.g)} />
            <p className="mt-2 truncate text-[12px] text-[#6B7280]">
              <span className="font-medium text-[#374151]">
                {big.name || "—"}
              </span>
              {" · "}
              {replay ? fmtTime(new Date(big.t)) : ago(big.t, now)}
            </p>
          </>
        ) : (
          <span className="text-[28px] font-bold text-[#D1D5DB]">—</span>
        )}
      </Tile>

      <Tile icon={Clock} label={t("caseDashboard.live.kpi.lastImpact")}>
        {stats.last ? (
          <>
            <span className="text-[28px] font-bold tabular-nums leading-none text-[#111827]">
              {replay
                ? fmtTime(new Date(stats.last.t))
                : ago(stats.last.t, now)}
            </span>
            <p className="mt-2 truncate text-[12px] text-[#6B7280]">
              <span className="font-medium text-[#374151]">
                {stats.last.name || "—"}
              </span>
              {" · "}
              <span
                style={{ color: SEVERITY_TEXT[severityIndex(stats.last.g)] }}
                className="font-semibold"
              >
                {gText(t, stats.last.g)}
              </span>
            </p>
          </>
        ) : (
          <span className="text-[28px] font-bold text-[#D1D5DB]">—</span>
        )}
      </Tile>
    </div>
  );
};
